import { ROLES } from '@/config/roles';
import { PermissionService } from '@/services/n8n/permission';
import { currentUser } from '@/services/user';
import type { CurrentUser } from '@/types';
import { getCookie, removeCookie, setCookie } from '@/utils/cookie';

/**
 * 认证服务类
 * 处理所有与用户认证相关的逻辑
 */
export class AuthService {
  private static readonly TOKEN_KEY = 'admin_access_token';
  private static readonly LOGIN_URL = 'https://id.snapmaker.com';
  private static isRedirecting = false;

  /**
   * 从URL参数中获取token
   */
  static getTokenFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }

  /**
   * 从Cookie中获取token
   */
  static getTokenFromCookie(): string | undefined {
    return getCookie(AuthService.TOKEN_KEY);
  }

  /**
   * 保存token到Cookie
   */
  static saveToken(token: string): void {
    setCookie(AuthService.TOKEN_KEY, token);
  }

  /**
   * 清除token
   */
  static clearToken(): void {
    removeCookie(AuthService.TOKEN_KEY);
  }

  /**
   * 清除URL中的token参数
   */
  static clearTokenFromUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  /**
   * 跳转到外部登录页面
   */
  static redirectToLogin(options?: {
    delayMs?: number;
    clearToken?: boolean;
  }): void {
    const { delayMs = 0, clearToken = true } = options ?? {};
    if (clearToken) {
      AuthService.clearToken();
    }
    if (AuthService.isRedirecting) {
      return;
    }
    AuthService.isRedirecting = true;
    const currentUrl = window.location.href;
    const redirectUrl = encodeURIComponent(currentUrl);
    const loginUrl = `${AuthService.LOGIN_URL}?redirect=${redirectUrl}`;
    if (delayMs > 0) {
      window.setTimeout(() => {
        window.location.href = loginUrl;
      }, delayMs);
      return;
    }
    window.location.href = loginUrl;
  }

  /**
   * 验证token并获取用户信息
   */
  static async validateTokenAndGetUser(
    token: string,
  ): Promise<CurrentUser | null> {
    try {
      // 调用用户信息接口验证token
      const userResponse = await currentUser(token);

      if (userResponse.code !== 200 || !userResponse.data) {
        // Failed to fetch user info
        return null;
      }

      const userData = userResponse.data;

      // 使用PermissionService获取用户权限信息
      let roleCode: string = ROLES.GUEST;
      let roleCodes: string[] = [];

      try {
        const users = await PermissionService.getUsers();
        // 用户可能有多个角色，每个角色返回一行记录
        const userInfoList = users.filter(
          (user) => user.email === userData.email,
        );

        if (userInfoList.length > 0) {
          // 收集所有角色代码（去重）
          roleCodes = [
            ...new Set(userInfoList.map((u) => u.role_code).filter(Boolean)),
          ];
          roleCode = roleCodes[0] || ROLES.GUEST;
        } else {
          // 如果用户不在权限系统中，但是snapmaker邮箱，设置为GUEST角色（只能canAccess）
          if (userData.email?.endsWith('@snapmaker.com')) {
            // User not in permission system, using GUEST role
            roleCode = ROLES.GUEST;
          } else {
            console.error(
              'Access denied: User not in snapmaker domain:',
              userData.email,
            );
            return null;
          }
        }
      } catch (permissionError) {
        console.error(
          'Failed to fetch user permissions, using fallback:',
          permissionError,
        );
        // 如果权限服务失败，但是snapmaker邮箱，设置为GUEST
        if (userData.email?.endsWith('@snapmaker.com')) {
          roleCode = ROLES.GUEST;
        } else {
          return null;
        }
      }

      // 返回用户信息，包含角色信息
      const mappedUser: CurrentUser = {
        ...userData,
        name: userData.nickname || userData.account,
        avatar: userData.icon,
        role_code: roleCode,
        role_codes: roleCodes.length > 0 ? roleCodes : [roleCode],
      };

      return mappedUser;
    } catch (error) {
      console.error('Token validation failed:', error);
      return null;
    }
  }

  /**
   * 完整的认证流程
   * 1. 从URL获取token
   * 2. 从Cookie获取token
   * 3. 验证token
   * 4. 如果没有有效token，跳转到登录页面
   */
  static async authenticate(): Promise<{
    user: CurrentUser | null;
    isAuthenticated: boolean;
    shouldRedirect: boolean;
  }> {
    try {
      let token: string | null = null;

      // 步骤1: 优先从URL获取token（OAuth回调处理）
      const urlToken = AuthService.getTokenFromUrl();
      if (urlToken) {
        console.log('Token found in URL, validating...');
        token = urlToken;
        // 保存token到Cookie
        AuthService.saveToken(urlToken);
        // 清除URL中的token参数
        AuthService.clearTokenFromUrl();
      } else {
        // 步骤2: 从Cookie获取token
        const cookieToken = AuthService.getTokenFromCookie();
        if (cookieToken) {
          console.log('Token found in cookie, validating...');
          token = cookieToken;
        }
      }

      // 步骤3: 如果有token，验证token
      if (token) {
        const user = await AuthService.validateTokenAndGetUser(token);
        if (user) {
          console.log('Authentication successful:', user);
          return {
            user,
            isAuthenticated: true,
            shouldRedirect: false,
          };
        } else {
          // token无效，清除并跳转登录
          console.log('Token validation failed, clearing token');
          AuthService.clearToken();
        }
      }

      // 步骤4: 没有有效token，需要跳转登录
      console.log('No valid token found, redirecting to login');
      return {
        user: null,
        isAuthenticated: false,
        shouldRedirect: true,
      };
    } catch (error) {
      console.error('Authentication process failed:', error);
      AuthService.clearToken();
      return {
        user: null,
        isAuthenticated: false,
        shouldRedirect: true,
      };
    }
  }

  /**
   * 登出
   */
  static logout(): void {
    AuthService.redirectToLogin();
  }
}
